import React, { useMemo, useState } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Phone, Mail, X, CheckCircle2, Eye } from "lucide-react";

const digitsOf = (s) => (s || "").replace(/\D/g, "");

/**
 * Unified customer fields — no Existing/New toggle. The new-customer form is
 * always shown; typing a name or phone surfaces matching existing customers,
 * and selecting one links it (customer_id) and autofills every field.
 * While linked the identity fields are locked ("Change" unlinks) so a linked
 * customer can never silently diverge from their record.
 */
const CustomerAutofill = ({
  values, onChange, customers = [], onSelectCustomer, onUnlink, onOpenCustomer,
  inputClassName = "input-glass", testIds = {},
}) => {
  const ids = {
    name: "customer-name-input", phone: "customer-phone-input",
    phone2: "customer-phone2-input", email: "customer-email-input",
    suggestions: "customer-suggestions", linked: "customer-linked-chip",
    unlink: "customer-unlink-btn", view: "customer-view-btn", ...testIds,
  };
  const [focusField, setFocusField] = useState(null);
  const linked = !!values.customer_id;

  const suggestions = useMemo(() => {
    if (linked || !focusField) return [];
    if (focusField === "name") {
      const q = (values.customer_name || "").trim().toLowerCase();
      if (q.length < 2) return [];
      return customers.filter((c) => (c.name || "").toLowerCase().includes(q)).slice(0, 6);
    }
    if (focusField === "phone") {
      const q = digitsOf(values.customer_phone);
      if (q.length < 3) return [];
      return customers
        .filter((c) => digitsOf(c.phone).includes(q) || digitsOf(c.secondary_phone).includes(q))
        .slice(0, 6);
    }
    return [];
  }, [linked, focusField, values.customer_name, values.customer_phone, customers]);

  const dropdown = (field) =>
    focusField === field && suggestions.length > 0 && (
      <div
        className="absolute z-50 left-0 right-0 top-full mt-1 rounded-md border border-border bg-popover shadow-xl overflow-hidden"
        data-testid={ids.suggestions}
      >
        <p className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/60">
          Existing customers — select to autofill
        </p>
        {suggestions.map((c) => (
          <button
            key={c.customer_id}
            type="button"
            onMouseDown={(e) => { e.preventDefault(); setFocusField(null); onSelectCustomer(c); }}
            className="w-full text-left px-3 py-2 hover:bg-muted/60 flex items-center justify-between gap-3"
            data-testid={`${ids.suggestions}-item-${c.customer_id}`}
          >
            <span className="min-w-0">
              <span className="block text-sm text-foreground font-medium truncate">{c.name}</span>
              {c.address && <span className="block text-[11px] text-muted-foreground truncate">{c.address}</span>}
            </span>
            <span className="text-xs text-muted-foreground whitespace-nowrap">{c.phone}</span>
          </button>
        ))}
      </div>
    );

  return (
    <div className="space-y-3">
      {linked && (
        <div
          className="flex items-center justify-between rounded-md border border-teal-500/40 bg-teal-500/10 px-3 py-2"
          data-testid={ids.linked}
        >
          <span className="flex items-center gap-2 text-sm text-teal-600 dark:text-teal-400 min-w-0">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span className="truncate">
              Existing customer: <span className="font-semibold text-foreground">{values.customer_name}</span>
            </span>
          </span>
          <span className="flex items-center gap-3 shrink-0">
            {onOpenCustomer && (
              <button
                type="button"
                onClick={onOpenCustomer}
                className="text-xs text-teal-600 dark:text-teal-400 hover:underline underline-offset-2 flex items-center gap-1 font-medium"
                data-testid={ids.view}
              >
                <Eye className="w-3 h-3" /> View / Edit
              </button>
            )}
            <button
              type="button"
              onClick={onUnlink}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              data-testid={ids.unlink}
            >
              <X className="w-3 h-3" /> Change
            </button>
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5 relative">
          <Label className="text-muted-foreground text-xs flex items-center gap-1">
            Name <span className="text-red-400">*</span>
          </Label>
          <Input
            value={values.customer_name || ""}
            onChange={(e) => onChange("customer_name", e.target.value)}
            onFocus={() => setFocusField("name")}
            onBlur={() => setFocusField((f) => (f === "name" ? null : f))}
            disabled={linked}
            className={inputClassName}
            placeholder="Type a name — existing customers will be suggested"
            autoComplete="off"
            data-testid={ids.name}
          />
          {dropdown("name")}
        </div>
        <div className="space-y-1.5 relative">
          <Label className="text-muted-foreground text-xs flex items-center gap-1">
            <Phone className="w-3 h-3" /> Phone <span className="text-red-400">*</span>
          </Label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Input
                value={values.customer_phone || ""}
                onChange={(e) => onChange("customer_phone", e.target.value)}
                onFocus={() => setFocusField("phone")}
                onBlur={() => setFocusField((f) => (f === "phone" ? null : f))}
                disabled={linked}
                className={inputClassName}
                placeholder="(555) 123-4567"
                autoComplete="off"
                data-testid={ids.phone}
              />
              {dropdown("phone")}
            </div>
            <Input
              value={values.customer_phone_ext || ""}
              onChange={(e) => onChange("customer_phone_ext", e.target.value.replace(/\D/g, "").slice(0, 10))}
              className={`${inputClassName} w-20 shrink-0`}
              placeholder="Ext."
              inputMode="numeric"
              autoComplete="off"
              title="Optional extension — used when calling this number"
              data-testid="customer-phone-ext"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs flex items-center gap-1">
            <Phone className="w-3 h-3" /> Secondary Phone
          </Label>
          <div className="flex gap-2">
            <Input
              value={values.customer_phone2 || ""}
              onChange={(e) => onChange("customer_phone2", e.target.value)}
              className={`${inputClassName} flex-1`}
              placeholder="(555) 987-6543 (optional)"
              data-testid={ids.phone2}
            />
            <Input
              value={values.customer_phone2_ext || ""}
              onChange={(e) => onChange("customer_phone2_ext", e.target.value.replace(/\D/g, "").slice(0, 10))}
              className={`${inputClassName} w-20 shrink-0`}
              placeholder="Ext."
              inputMode="numeric"
              autoComplete="off"
              title="Optional extension — used when calling this number"
              data-testid="customer-phone2-ext"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs flex items-center gap-1">
            <Mail className="w-3 h-3" /> Email
          </Label>
          <Input
            type="email"
            value={values.customer_email || ""}
            onChange={(e) => onChange("customer_email", e.target.value)}
            disabled={linked}
            className={inputClassName}
            placeholder="email@example.com"
            data-testid={ids.email}
          />
        </div>
      </div>
    </div>
  );
};

export default CustomerAutofill;
